/**
 * A settings PATCH applied to the cached settings — what the Preferences
 * screens show the instant a switch moves, before the server answers.
 *
 * It mirrors the server's merge for what the apps send: flat notification
 * switches, the whole quiet-hours window, matrix CELLS (with `categories`
 * following as "any channel on"), and privacy / accessibility / app-scoped
 * preferences. A `categories` PATCH (business-web saves
 * one) is recorded as sent; how it moves the matrix is the server's rule, and
 * its answer replaces this guess on success.
 */
import { NOTIFICATION_BUCKETS } from '@tonnie/types';

import type { UpdateSettingsInput, UserSettings } from '../services/user.service';

export function applySettingsPatch(
  current: UserSettings,
  patch: UpdateSettingsInput
): UserSettings {
  const { quietHours, matrix, categories, ...flat } = patch.notifications ?? {};
  const notifications: UserSettings['notifications'] = { ...current.notifications, ...flat };

  if (quietHours && current.notifications.quietHours) {
    notifications.quietHours = { ...current.notifications.quietHours, ...quietHours };
  }

  if (matrix && current.notifications.matrix) {
    const next = { ...current.notifications.matrix };
    for (const bucket of NOTIFICATION_BUCKETS) {
      const cell = matrix[bucket];
      if (cell) next[bucket] = { ...next[bucket], ...cell };
    }
    notifications.matrix = next;
    notifications.categories = {
      hero: next.hero.email || next.hero.push,
      system: next.system.email || next.system.push,
      social: next.social.email || next.social.push,
      alert: next.alert.email || next.alert.push,
    };
  } else if (categories && current.notifications.categories) {
    notifications.categories = { ...current.notifications.categories, ...categories };
  }

  const preferences = { ...(current.preferences ?? {}) };
  for (const [key, value] of Object.entries(patch.preferences ?? {})) {
    const existing = preferences[key];
    preferences[key] =
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      existing &&
      typeof existing === 'object' &&
      !Array.isArray(existing)
        ? { ...(existing as Record<string, unknown>), ...(value as Record<string, unknown>) }
        : value;
  }

  return {
    ...current,
    notifications,
    privacy: { ...current.privacy, ...(patch.privacy ?? {}) },
    accessibility: current.accessibility
      ? { ...current.accessibility, ...(patch.accessibility ?? {}) }
      : current.accessibility,
    preferences,
  };
}
